// Public Careers page.
//
// Layout matches the design system in careers.html:
//   1. Dark-ink hero — eyebrow / split title / lede / two CTAs / animated rings
//   2. Stats bar — four KPI cards
//   3. Why TrustLab — six pillar cards in a 2/3-col grid
//   4. Openings — dark section, dynamic filter chips (department + location),
//      list of admin-curated job cards
//   5. Apply form — first-name/last-name… split fields, CV uploader, consent
//   6. Enquiry card — fallback "Email HR" CTA
//
// Job openings come from /api/careers/openings (admin-curated). The Apply
// form POSTs multipart/form-data including the CV — backend pushes it to
// Cloudinary's "raw" bucket and saves a JobApplication row.

import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import {
  useListCareerOpeningsQuery,
  useSubmitCareerApplicationMutation,
} from '../store/siteContentApi';

// ─── Design tokens (mirror careers.html) ───────────────────────
const C = {
  teal:      '#00B49A',
  tealDark:  '#00897A',
  gold:      '#F0B429',
  ink:       '#1A3A2A',
  inkSoft:   '#2C4A3A',
  paper:     '#FAF8F3',
  paper2:    '#F2EEE4',
  line:      '#D9D3C4',
  muted:     '#6B7570',
  danger:    '#B0421A',
};

// Static brand content — six "Why TrustLab" pillars + four stats + HR contact.
// These are marketing copy that rarely changes; if the org wants them
// editable later we can wire a singleton like LegalDocs.
const PILLARS = [
  { num: '01', title: 'Work that affects real patients',
    body: "From the first phlebotomy to the final report, your work feeds directly into a doctor's diagnosis. Your accuracy is someone's clarity." },
  { num: '02', title: 'Built for growth, by design',
    body: 'A growing branch network. A documented role framework. Lateral moves across disciplines, vertical moves into leadership — both are real here.' },
  { num: '03', title: 'Performance, recognised in pay',
    body: 'Fixed base + monthly variable + quarterly department bonus + annual increment + skill-tier progression. Your effort shows up in your bank account.' },
  { num: '04', title: 'NABL-grade quality, every day',
    body: 'Continuous EQA participation, structured CME, ISO 15189 systems, and SOPs that respect the work. Bring rigour, take rigour with you.' },
  { num: '05', title: 'Transparency you can plan around',
    body: 'Published increment policy, KPI scorecards visible to you, clear designation bands by city tier. No surprises, no ceiling games.' },
  { num: '06', title: 'A team that has your back',
    body: 'Mentorship from senior pathologists and lab heads, structured onboarding, and a culture where asking is rewarded — not punished.' },
];

const STATS = [
  { value: '27', label: 'Defined Roles', accent: 'value' },
  { value: 'NABL', label: 'ISO 15189 Accredited', accent: null },
  { value: '5', label: 'Compensation Levers', accent: 'value' },
  { value: '100%', label: 'Quality-First Culture', accent: 'tail' }, // % is tealed
];

const HR_EMAIL = 'hr@mytrustlab.com';
const MAX_CV_BYTES = 5 * 1024 * 1024;

// ─── Hero animated rings ──────────────────────────────────────
const HeroVisual = ({ branchesCount }) => (
  <Box
    aria-hidden
    sx={{
      position: 'relative',
      aspectRatio: '1 / 1',
      maxWidth: 480,
      width: '100%',
      mx: 'auto',
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          inset: i * 40,
          border: `2px solid ${i === 1 ? 'rgba(240,180,41,0.2)' : 'rgba(0,180,154,' + (0.3 - i * 0.075) + ')'}`,
          borderRadius: '50%',
          animation: `careers-spin ${[60, 45, 30][i]}s linear infinite`,
          animationDirection: i === 1 ? 'reverse' : 'normal',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 12, height: 12,
            background: i === 1 ? C.gold : C.teal,
            borderRadius: '50%',
            top: -6, left: '50%',
            transform: 'translateX(-50%)',
          },
        }}
      />
    ))}
    <Box
      sx={{
        position: 'absolute',
        inset: { xs: 80, sm: 120 },
        bgcolor: 'rgba(0,180,154,0.08)',
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        textAlign: 'center',
        p: 3,
      }}
    >
      <Box>
        <Box sx={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: { xs: 56, sm: 76 },
          lineHeight: 1,
          color: C.teal,
          letterSpacing: 2,
        }}>
          {branchesCount}
        </Box>
        <Box sx={{
          fontFamily: "'Space Mono', monospace",
          fontSize: 11,
          letterSpacing: '2px',
          color: C.paper,
          textTransform: 'uppercase',
          mt: 0.5,
        }}>
          Branches · Growing
        </Box>
      </Box>
    </Box>
    <Box component="style" dangerouslySetInnerHTML={{ __html: '@keyframes careers-spin { to { transform: rotate(360deg); } }' }} />
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────
const Careers = () => {
  const { data: openings = [], isLoading: openingsLoading } = useListCareerOpeningsQuery();
  const [submit, { isLoading: submitting }] = useSubmitCareerApplicationMutation();

  // Derive filter chips from the openings list — only show departments /
  // locations that actually have a posting, so chips never lie.
  const departments = useMemo(() => {
    const set = new Set(openings.map((o) => o.department).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [openings]);
  const locations = useMemo(() => {
    const set = new Set(openings.map((o) => o.location).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [openings]);

  const [activeDept, setActiveDept] = useState('all');
  const [activeLoc,  setActiveLoc]  = useState('all');

  const filteredOpenings = useMemo(() =>
    openings.filter((o) =>
      (activeDept === 'all' || o.department === activeDept) &&
      (activeLoc  === 'all' || o.location   === activeLoc)),
    [openings, activeDept, activeLoc]);

  // ─── Apply form state ───
  const initialForm = {
    full_name: '', email: '', phone: '',
    position: '', experience: '',
    current_location: '', qualification: '', notice_period: '',
    message: '', source: '',
    consent: false,
  };
  const [form, setForm] = useState(initialForm);
  const [cvFile, setCvFile] = useState(null);
  const [dragHover, setDragHover] = useState(false);
  const [status, setStatus] = useState({ kind: '', message: '' });
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const onPickCv = (file) => {
    if (!file) { setCvFile(null); return; }
    if (file.size > MAX_CV_BYTES) {
      setStatus({ kind: 'error', message: 'CV is larger than 5 MB. Please upload a smaller file.' });
      setCvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setStatus({ kind: '', message: '' });
    setCvFile(file);
  };

  // Pre-select position when user clicks "Apply" on an opening card.
  const applyToOpening = (opening) => {
    setForm((f) => ({ ...f, position: opening.job_id }));
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => document.getElementById('careers-full-name')?.focus(), 600);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ kind: '', message: '' });

    // Native validation first — checks required + pattern on email/phone.
    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }
    if (!cvFile) {
      setStatus({ kind: 'error', message: 'Please attach your CV (PDF / DOC / DOCX).' });
      return;
    }
    if (!form.consent) {
      setStatus({ kind: 'error', message: 'Please tick the consent box to submit your application.' });
      return;
    }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v == null ? '' : String(v)));
    fd.append('cv', cvFile);
    fd.append('source_page', window.location.href);

    try {
      const resp = await submit(fd).unwrap();
      setStatus({
        kind: 'success',
        message: resp?.message || "Application received. We'll acknowledge your submission within 3 working days.",
      });
      setForm(initialForm);
      setCvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const fallbackSubject = openings.find((o) => o.job_id === form.position)?.title || 'Open application';
      setStatus({
        kind: 'error',
        message:
          (err?.data?.message || 'Could not submit through the form.') +
          ' You can email your CV directly to ' + HR_EMAIL,
        mailto: `mailto:${HR_EMAIL}?subject=${encodeURIComponent('Application — ' + fallbackSubject)}`,
      });
    }
  };

  // ─── Rendering ────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: C.paper, color: C.ink }}>
      {/* ─── HERO ─── */}
      <Box
        sx={{
          bgcolor: C.ink, color: C.paper,
          pt: { xs: 8, md: 13 }, pb: { xs: 12, md: 15 },
          px: { xs: 2.5, md: 4 },
          position: 'relative', overflow: 'hidden',
          '&::before': {
            content: '""', position: 'absolute', inset: 0,
            background:
              'radial-gradient(circle at 80% 15%, rgba(0,180,154,0.22), transparent 50%),' +
              'radial-gradient(circle at 10% 95%, rgba(240,180,41,0.14), transparent 55%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""', position: 'absolute',
            bottom: -1, left: 0, right: 0, height: 80,
            background: `linear-gradient(to bottom, transparent, ${C.paper})`,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 12, letterSpacing: '3px',
                color: C.gold, textTransform: 'uppercase', mb: 3,
              }}>
                Careers at TrustLab
              </Box>
              <Box component="h1" sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: { xs: 44, sm: 60, md: 80 },
                lineHeight: 0.95, letterSpacing: '1.5px',
                m: 0, mb: 2.5, color: C.paper,
              }}>
                Build a career<br />
                that <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>matters</Box>.
              </Box>
              <Box sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 15.5, lineHeight: 1.6,
                color: 'rgba(250,248,243,0.82)',
                maxWidth: 480, mb: 4,
              }}>
                Every test we run shapes a clinical decision. Every report we deliver carries
                someone's peace of mind. Join an NABL-accredited lab network where the work you do
                actually changes outcomes.
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <CtaLink href="#openings" variant="primary">See open roles →</CtaLink>
                <CtaLink href="#why-tdpl" variant="ghost">Why TrustLab</CtaLink>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <HeroVisual branchesCount={openings.length || 14} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── STATS BAR ─── */}
      <Box sx={{
        bgcolor: C.paper, py: 6, px: { xs: 2.5, md: 4 },
        borderBottom: `1px solid ${C.line}`,
        mt: '-40px', position: 'relative', zIndex: 2,
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {STATS.map((s) => (
              <Grid item xs={6} md={3} key={s.label} sx={{ textAlign: 'center' }}>
                <Box sx={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: { xs: 36, md: 48 },
                  lineHeight: 1, letterSpacing: '1px',
                  color: C.ink,
                }}>
                  {s.accent === 'value' ? <Box component="span" sx={{ color: C.teal }}>{s.value}</Box> :
                   s.accent === 'tail'  ? <>100<Box component="span" sx={{ color: C.teal }}>%</Box></> :
                   s.value}
                </Box>
                <Box sx={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 11, letterSpacing: '2px',
                  color: C.muted, textTransform: 'uppercase', mt: 1,
                }}>
                  {s.label}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── WHY TRUSTLAB ─── */}
      <Container
        id="why-tdpl"
        maxWidth="lg"
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 2.5, md: 4 },
          // Offset for the sticky navbar when the About page deep-links here.
          scrollMarginTop: { xs: 72, md: 96 },
        }}
      >
        <SectionHeading
          eyebrow="Why join us"
          title={<>Six reasons to choose <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>TrustLab</Box>.</>}
          lede="We don't sell perks. We offer something rarer: clinically meaningful work, a transparent path forward, and pay that actually rewards what you contribute."
        />

        <Grid container spacing={0} sx={{
          border: `1px solid ${C.line}`, borderRadius: '6px',
          overflow: 'hidden', bgcolor: C.line,
        }}>
          {PILLARS.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p.num} sx={{
              bgcolor: C.paper,
              p: { xs: 4, md: 5 },
              transition: 'background 0.2s',
              '&:hover': { bgcolor: C.paper2 },
            }}>
              <Box sx={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 11, letterSpacing: '2px', color: C.gold, mb: 3,
              }}>
                {p.num}
              </Box>
              <Box component="h3" sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 22, letterSpacing: '1px', color: C.ink,
                m: 0, mb: 1.25, lineHeight: 1.1,
              }}>
                {p.title}
              </Box>
              <Box sx={{ fontSize: 14, color: C.inkSoft, m: 0, lineHeight: 1.55 }}>
                {p.body}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ─── OPENINGS ─── */}
      <Box
        id="openings"
        sx={{
          bgcolor: C.ink,
          color: C.paper,
          py: { xs: 8, md: 12 },
          px: { xs: 2.5, md: 4 },
          // Offset for the sticky navbar when the About page deep-links here.
          scrollMarginTop: { xs: 72, md: 96 },
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Current Openings"
            eyebrowColor={C.gold}
            title={<>Roles we're <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>hiring</Box> for.</>}
            lede="Filter by department or location. Don't see your role? Send us your CV anyway — we keep strong profiles in our talent pool and reach back when something opens up."
            inverse
          />

          {/* Department filter */}
          {departments.length > 1 && (
            <FilterBar
              label="Department"
              options={departments}
              active={activeDept}
              onSelect={setActiveDept}
            />
          )}
          {/* Location filter */}
          {locations.length > 1 && (
            <FilterBar
              label="Location"
              options={locations}
              active={activeLoc}
              onSelect={setActiveLoc}
            />
          )}

          {/* Openings list */}
          {openingsLoading ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={130} sx={{ bgcolor: 'rgba(250,248,243,0.06)' }} />
              ))}
            </Box>
          ) : filteredOpenings.length === 0 ? (
            <Box sx={{
              bgcolor: 'rgba(250,248,243,0.03)',
              border: '1px dashed rgba(250,248,243,0.2)',
              borderRadius: '6px',
              p: 6, textAlign: 'center',
              color: 'rgba(250,248,243,0.6)',
            }}>
              <Box sx={{ mb: 1 }}>No openings match these filters right now.</Box>
              <Box>
                You can still{' '}
                <Box component="a" href="#apply" sx={{
                  color: C.teal, textDecoration: 'underline', cursor: 'pointer',
                  '&:hover': { color: C.gold },
                }}>
                  submit an open application
                </Box>{' '}
                — we'll reach out when something matches.
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {filteredOpenings.map((o) => (
                <OpeningCard key={o._id || o.job_id} opening={o} onApply={() => applyToOpening(o)} />
              ))}
            </Box>
          )}
        </Container>
      </Box>

      {/* ─── APPLY ─── */}
      <Box id="apply" sx={{ bgcolor: C.paper2, py: { xs: 8, md: 12 }, px: { xs: 2.5, md: 4 } }}>
        <Container maxWidth="md">
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <SectionHeading
                eyebrow="Apply now"
                title={<>Send us <Box component="em" sx={{ fontStyle: 'normal', color: C.teal }}>your CV</Box>.</>}
                lede="Whether you're applying for a posted role or making an open application, this form goes directly to our HR team. We aim to acknowledge every application within 3 working days."
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                bgcolor: C.paper,
                border: `1px solid ${C.line}`,
                borderRadius: '6px',
                p: { xs: 4, md: 6 },
              }}>
                <Box component="form" ref={formRef} onSubmit={onSubmit} noValidate>
                  <FormGrid>
                    <FormField label="Full Name" required>
                      <Input id="careers-full-name" name="full_name" type="text"
                        value={form.full_name} onChange={setField('full_name')} required autoComplete="name" />
                    </FormField>
                    <FormField label="Email" required>
                      <Input name="email" type="email"
                        value={form.email} onChange={setField('email')} required autoComplete="email" />
                    </FormField>
                  </FormGrid>

                  <FormGrid>
                    <FormField label="Phone Number" required hint="10-digit Indian mobile or include country code">
                      <Input name="phone" type="tel" pattern="[0-9+\s\-]{10,15}"
                        value={form.phone} onChange={setField('phone')} required autoComplete="tel" />
                    </FormField>
                    <FormField label="Applying For" required>
                      <Select name="position" value={form.position} onChange={setField('position')} required>
                        <option value="">Select a role</option>
                        <option value="open">Open application (any suitable role)</option>
                        {openings.map((o) => (
                          <option key={o._id || o.job_id} value={o.job_id}>
                            {o.title}{o.location ? ' — ' + o.location : ''}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                  </FormGrid>

                  <FormGrid>
                    <FormField label="Years of Experience" required>
                      <Select name="experience" value={form.experience} onChange={setField('experience')} required>
                        <option value="">Select experience</option>
                        <option value="fresher">Fresher (0 years)</option>
                        <option value="0-2">0–2 years</option>
                        <option value="2-5">2–5 years</option>
                        <option value="5-10">5–10 years</option>
                        <option value="10+">10+ years</option>
                      </Select>
                    </FormField>
                    <FormField label="Current Location" required>
                      <Input name="current_location" type="text" placeholder="e.g., Hyderabad"
                        value={form.current_location} onChange={setField('current_location')} required />
                    </FormField>
                  </FormGrid>

                  <FormGrid>
                    <FormField label="Highest Qualification">
                      <Input name="qualification" type="text" placeholder="e.g., DMLT / B.Sc / MD Pathology"
                        value={form.qualification} onChange={setField('qualification')} />
                    </FormField>
                    <FormField label="Notice Period">
                      <Select name="notice_period" value={form.notice_period} onChange={setField('notice_period')}>
                        <option value="">Select notice period</option>
                        <option value="immediate">Immediate</option>
                        <option value="15-days">Within 15 days</option>
                        <option value="30-days">30 days</option>
                        <option value="60-days">60 days</option>
                        <option value="90-days">90 days</option>
                        <option value="not-applicable">Not applicable</option>
                      </Select>
                    </FormField>
                  </FormGrid>

                  <FormField label="Upload CV" required>
                    <CvUploader
                      file={cvFile}
                      dragHover={dragHover}
                      setDragHover={setDragHover}
                      onPick={onPickCv}
                      inputRef={fileInputRef}
                    />
                  </FormField>

                  <Box sx={{ height: 16 }} />

                  <FormField label="Cover Note / Why TrustLab?">
                    <Textarea name="message" value={form.message} onChange={setField('message')}
                      placeholder="Tell us in a few sentences why you'd like to join us, or anything we should know before reading your CV." />
                  </FormField>

                  <Box sx={{ height: 16 }} />

                  <FormField label="How did you hear about us?">
                    <Select name="source" value={form.source} onChange={setField('source')}>
                      <option value="">Select source (optional)</option>
                      <option>LinkedIn</option>
                      <option>Naukri / Job portal</option>
                      <option>TrustLab website</option>
                      <option>Employee referral</option>
                      <option>College / placement</option>
                      <option>Walk-in / branch visit</option>
                      <option>Other</option>
                    </Select>
                  </FormField>

                  {/* Consent */}
                  <Box sx={{
                    display: 'flex', gap: 1.5, alignItems: 'flex-start',
                    bgcolor: C.paper2, p: 2, borderRadius: 1,
                    my: 3, fontSize: 13, color: C.inkSoft,
                  }}>
                    <Box component="input" type="checkbox" name="consent" id="careers-consent"
                      checked={form.consent} onChange={setField('consent')} required
                      sx={{ mt: '3px', accentColor: C.teal }}
                    />
                    <Box component="label" htmlFor="careers-consent" sx={{ flex: 1, cursor: 'pointer' }}>
                      I consent to TrustLab Diagnostics processing the personal data submitted in this
                      form for the purpose of evaluating my candidature, in accordance with the{' '}
                      <Box component="a" href="/privacy" sx={{ color: C.teal, textDecoration: 'underline' }}>
                        Privacy Policy
                      </Box>. I understand my data will be retained in TrustLab's applicant tracking
                      system per the company's retention schedule.{' '}
                      <Box component="span" sx={{ color: C.teal }}>*</Box>
                    </Box>
                  </Box>

                  <Box
                    component="button"
                    type="submit"
                    disabled={submitting}
                    sx={{
                      bgcolor: C.ink, color: C.paper,
                      border: 'none', p: '16px 32px', borderRadius: 1,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                      letterSpacing: 2, textTransform: 'uppercase',
                      transition: 'all 0.15s', width: '100%',
                      opacity: submitting ? 0.5 : 1,
                      '&:hover': { bgcolor: submitting ? C.ink : C.teal },
                    }}
                  >
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </Box>

                  {status.kind && (
                    <Box sx={{
                      mt: 2, p: '14px 18px', borderRadius: 1, fontSize: 14,
                      bgcolor: status.kind === 'success' ? 'rgba(0,180,154,0.1)' : 'rgba(176,66,26,0.1)',
                      color:   status.kind === 'success' ? C.tealDark : C.danger,
                      border:  `1px solid ${status.kind === 'success' ? C.teal : C.danger}`,
                    }}>
                      <Box component="strong" sx={{ display: 'block', mb: 0.5 }}>
                        {status.kind === 'success' ? 'Application received.' : "Couldn't submit."}
                      </Box>
                      {status.message}
                      {status.mailto && (
                        <>
                          {' '}
                          <Box component="a" href={status.mailto} sx={{ color: 'inherit', textDecoration: 'underline' }}>
                            Email us
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Enquiry card */}
            <Grid item xs={12}>
              <Box sx={{
                bgcolor: C.ink, color: C.paper,
                borderRadius: '6px', p: { xs: 4, md: 5 },
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                gap: 4, alignItems: 'center',
              }}>
                <Box>
                  <Box component="h3" sx={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: 24, letterSpacing: '1px',
                    m: 0, mb: 1, color: C.gold,
                  }}>
                    Have a question instead?
                  </Box>
                  <Box sx={{ m: 0, color: 'rgba(250,248,243,0.75)', fontSize: 14, lineHeight: 1.55 }}>
                    For general HR queries, partnership requests, or campus recruitment programmes,
                    reach out to our People team directly. We respond within 2 working days.
                  </Box>
                </Box>
                <Box component="a"
                  href={`mailto:${HR_EMAIL}?subject=Careers%20Enquiry`}
                  sx={{
                    bgcolor: C.teal, color: C.paper,
                    textDecoration: 'none',
                    p: '14px 24px', borderRadius: 1,
                    fontSize: 13, fontWeight: 700, letterSpacing: '1.5px',
                    textTransform: 'uppercase', whiteSpace: 'nowrap',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: C.gold, color: C.ink },
                  }}>
                  Email HR →
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

// ─── Sub-components ───────────────────────────────────────────
const CtaLink = ({ href, variant, children }) => (
  <Box component="a" href={href} sx={{
    p: '14px 28px', borderRadius: 1,
    textDecoration: 'none', fontSize: 13, fontWeight: 700,
    letterSpacing: '1.5px', textTransform: 'uppercase',
    transition: 'all 0.15s',
    display: 'inline-flex', alignItems: 'center', gap: 1,
    ...(variant === 'primary' ? {
      bgcolor: '#00B49A', color: '#FAF8F3',
      '&:hover': { bgcolor: '#FAF8F3', color: '#1A3A2A' },
    } : {
      bgcolor: 'transparent', color: '#FAF8F3',
      border: '1px solid rgba(250,248,243,0.3)',
      '&:hover': { borderColor: '#F0B429', color: '#F0B429' },
    }),
  }}>
    {children}
  </Box>
);

const SectionHeading = ({ eyebrow, eyebrowColor, title, lede, inverse }) => (
  <Box sx={{ mb: { xs: 5, md: 7 } }}>
    <Box sx={{
      fontFamily: "'Space Mono', monospace",
      fontSize: 12, letterSpacing: '3px',
      color: eyebrowColor || C.teal,
      textTransform: 'uppercase', mb: 2,
    }}>
      {eyebrow}
    </Box>
    <Box component="h2" sx={{
      fontFamily: "'Bebas Neue', sans-serif",
      fontSize: { xs: 30, sm: 40, md: 56 },
      lineHeight: 1.0, letterSpacing: '1px',
      color: inverse ? C.paper : C.ink,
      m: 0, mb: 2.5,
    }}>
      {title}
    </Box>
    {lede && (
      <Box sx={{
        fontSize: 15.5, maxWidth: 640, lineHeight: 1.6,
        color: inverse ? 'rgba(250,248,243,0.7)' : C.inkSoft,
      }}>
        {lede}
      </Box>
    )}
  </Box>
);

const FilterBar = ({ label, options, active, onSelect }) => (
  <Box sx={{
    display: 'flex', flexWrap: 'wrap', gap: 1.5,
    mb: 4, alignItems: 'center',
  }}>
    <Box sx={{
      fontFamily: "'Space Mono', monospace",
      fontSize: 11, letterSpacing: '2px',
      color: C.gold, textTransform: 'uppercase', mr: 1,
    }}>
      {label}
    </Box>
    {options.map((opt) => (
      <Box key={opt} component="button" type="button"
        onClick={() => onSelect(opt)}
        sx={{
          bgcolor: active === opt ? C.teal : 'transparent',
          color: active === opt ? C.paper : 'rgba(250,248,243,0.75)',
          border: `1px solid ${active === opt ? C.teal : 'rgba(250,248,243,0.2)'}`,
          fontWeight: active === opt ? 700 : 400,
          px: 2, py: 1, borderRadius: 24,
          fontSize: 13, fontFamily: 'inherit',
          cursor: 'pointer', transition: 'all 0.15s',
          '&:hover': { borderColor: C.teal, color: C.paper },
        }}>
        {opt === 'all' ? 'All' : opt}
      </Box>
    ))}
  </Box>
);

const OpeningCard = ({ opening, onApply }) => (
  <Box sx={{
    bgcolor: 'rgba(250,248,243,0.04)',
    border: '1px solid rgba(250,248,243,0.1)',
    borderRadius: '6px',
    p: 3.5,
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
    gap: 3, alignItems: 'center',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: C.teal,
      bgcolor: 'rgba(0,180,154,0.06)',
      transform: { sm: 'translateX(4px)' },
    },
  }}>
    <Box>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 2,
        fontFamily: "'Space Mono', monospace",
        fontSize: 11, letterSpacing: '1.5px',
        color: C.gold, textTransform: 'uppercase', mb: 1.25,
      }}>
        {[opening.department, opening.location, opening.experience, opening.type]
          .filter(Boolean)
          .map((part, i, arr) => (
            <Box component="span" key={i}>
              {part}{i < arr.length - 1 && (
                <Box component="span" sx={{ color: 'rgba(240,180,41,0.4)', ml: 2 }}>·</Box>
              )}
            </Box>
          ))}
      </Box>
      <Box component="h3" sx={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: 21, letterSpacing: '0.5px',
        m: 0, mb: 0.75, lineHeight: 1.15,
        color: C.paper,
      }}>
        {opening.title}
      </Box>
      <Box sx={{ fontSize: 13.5, color: 'rgba(250,248,243,0.65)', m: 0, maxWidth: 640, lineHeight: 1.55 }}>
        {opening.description}
      </Box>
    </Box>
    <Box component="button" type="button" onClick={onApply}
      sx={{
        bgcolor: C.teal, color: C.paper, border: 'none',
        p: '14px 24px', borderRadius: 1, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
        letterSpacing: '1.5px', textTransform: 'uppercase',
        transition: 'background 0.15s', whiteSpace: 'nowrap',
        width: { xs: '100%', sm: 'auto' },
        '&:hover': { bgcolor: C.gold, color: C.ink },
      }}>
      Apply →
    </Box>
  </Box>
);

const FormGrid = ({ children }) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
    gap: 2.5, mb: 2.5,
  }}>
    {children}
  </Box>
);

const FormField = ({ label, required, hint, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Box component="label" sx={{
      fontFamily: "'Space Mono', monospace",
      fontSize: 11, letterSpacing: '1.5px',
      color: C.ink, textTransform: 'uppercase',
      mb: 1, fontWeight: 700,
    }}>
      {label}{required && <Box component="span" sx={{ color: C.teal, ml: 0.5 }}>*</Box>}
    </Box>
    {children}
    {hint && (
      <Box sx={{ fontSize: 12, color: C.muted, mt: 0.75 }}>{hint}</Box>
    )}
  </Box>
);

const inputBaseSx = {
  bgcolor: C.paper,
  border: `1px solid ${C.line}`,
  p: '12px 14px', borderRadius: 1,
  fontFamily: 'inherit', fontSize: 15,
  color: C.ink, outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:focus': {
    borderColor: C.teal,
    boxShadow: '0 0 0 3px rgba(0,180,154,0.12)',
  },
};

const Input = (props) => <Box component="input" {...props} sx={inputBaseSx} />;

const Select = (props) => (
  <Box component="select" {...props} sx={inputBaseSx}>
    {props.children}
  </Box>
);

const Textarea = (props) => (
  <Box component="textarea" {...props} sx={{ ...inputBaseSx, minHeight: 120, resize: 'vertical' }} />
);

const CvUploader = ({ file, dragHover, setDragHover, onPick, inputRef }) => {
  // Bind the wrapper as the drop target — file input fills it absolutely.
  useEffect(() => {
    const el = inputRef.current?.parentElement;
    if (!el) return undefined;
    const noop = (e) => { e.preventDefault(); };
    const enter = (e) => { noop(e); setDragHover(true); };
    const leave = (e) => { noop(e); setDragHover(false); };
    const drop  = (e) => {
      noop(e); setDragHover(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) onPick(f);
    };
    el.addEventListener('dragover', enter);
    el.addEventListener('dragenter', enter);
    el.addEventListener('dragleave', leave);
    el.addEventListener('drop', drop);
    return () => {
      el.removeEventListener('dragover', enter);
      el.removeEventListener('dragenter', enter);
      el.removeEventListener('dragleave', leave);
      el.removeEventListener('drop', drop);
    };
  }, [inputRef, onPick, setDragHover]);

  const hasFile = !!file;
  return (
    <Box sx={{
      border: `2px dashed ${(hasFile || dragHover) ? C.teal : C.line}`,
      borderStyle: hasFile ? 'solid' : 'dashed',
      borderRadius: '6px',
      p: '32px 24px', textAlign: 'center',
      bgcolor: hasFile || dragHover ? 'rgba(0,180,154,0.05)' : C.paper2,
      cursor: 'pointer', transition: 'all 0.2s',
      position: 'relative',
    }}>
      <Box component="input" ref={inputRef} type="file" accept=".pdf,.doc,.docx"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
        sx={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
      />
      <Box sx={{
        width: 48, height: 48, mx: 'auto', mb: 1.5,
        bgcolor: C.ink, color: C.gold,
        borderRadius: '50%',
        display: 'grid', placeItems: 'center',
        fontSize: 24,
      }}>
        ↑
      </Box>
      <Box sx={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: 18, letterSpacing: '1px',
        color: C.ink, mb: 0.5,
      }}>
        {hasFile ? file.name : 'Drop your CV here'}
      </Box>
      <Box sx={{
        fontSize: 12, color: C.muted,
        fontFamily: "'Space Mono', monospace", letterSpacing: '1px',
      }}>
        {hasFile
          ? `${(file.size / 1024).toFixed(0)} KB · Click to change`
          : 'PDF, DOC or DOCX · Max 5MB'}
      </Box>
    </Box>
  );
};

export default Careers;
